import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** Merge conditional classes, with later Tailwind utilities winning. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const TAKA = "\u09F3";

export function formatTaka(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "—";
  return TAKA + Number(amount).toLocaleString("en-US");
}

/** Compact form for KPI tiles, where a full number would overflow. */
export function formatTakaCompact(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "—";
  const value = Number(amount);
  if (Math.abs(value) >= 10_000_000) return TAKA + (value / 10_000_000).toFixed(2) + "Cr";
  if (Math.abs(value) >= 100_000) return TAKA + (value / 100_000).toFixed(2) + "L";
  return TAKA + value.toLocaleString("en-US");
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("en-US");
}

function parseSqlDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  // SQLite stores "YYYY-MM-DD HH:MM:SS"; some engines reject the space separator.
  const normalised = value.includes("T") ? value : value.replace(" ", "T") + "Z";
  const date = new Date(normalised);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(value: string | null | undefined): string {
  const date = parseSqlDate(value);
  if (!date) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Dhaka",
  });
}

export function formatDateTime(value: string | null | undefined): string {
  const date = parseSqlDate(value);
  if (!date) return "—";
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Dhaka",
  });
}

/**
 * Relative time, because "3 minutes ago" is what an operator actually needs when
 * scanning a live order queue. Falls back to an absolute date past a week.
 */
export function formatRelative(value: string | null | undefined): string {
  const date = parseSqlDate(value);
  if (!date) return "—";

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  // Negative durations (a clock-skewed or future timestamp) satisfied every
  // threshold below and rendered as "-3 min ago".
  if (seconds <= 45) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.round(seconds / 86_400)}d ago`;
  return formatDate(value);
}

/** Bytes, for the media library. */
export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = Number(bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
}

/** A URL-safe slug suggestion. Mirrors the server's slug rule. */
export function slugify(input: string): string {
  return input
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 160);
}

/* ------------------------------------------------------------ status tone */

export type Tone = "neutral" | "success" | "warning" | "danger" | "info";

/**
 * Order and payment statuses map to SEMANTIC tones, never to raw colours.
 * The previous admin used emerald/rose/slate/gray Tailwind hues for these, which
 * is how 96 off-palette colours ended up in the codebase.
 */
const ORDER_TONES: Record<string, Tone> = {
  pending: "warning",
  confirmed: "info",
  processing: "info",
  shipped: "info",
  delivered: "success",
  cancelled: "danger",
  refunded: "neutral",
};

const PAYMENT_TONES: Record<string, Tone> = {
  pending: "warning",
  paid: "success",
  partial: "info",
  refunded: "neutral",
  failed: "danger",
};

export function orderTone(status: string): Tone {
  return ORDER_TONES[status] ?? "neutral";
}

export function paymentTone(status: string): Tone {
  return PAYMENT_TONES[status] ?? "neutral";
}

/** Title-case a snake or kebab value for display. */
export function humanise(value: string | null | undefined): string {
  if (!value) return "—";
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/**
 * Query string for a CSV export link.
 *
 * Pagination is dropped on purpose: an export should contain everything the
 * current filters match, not just the page being viewed. Sort order is kept, so
 * the file arrives in the order the operator was looking at.
 */
export function exportQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (key === "page" || key === "per_page") continue;
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  return search.toString();
}
